import React, {Dispatch, SetStateAction, useState} from 'react';
import { Customer } from '../models';
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, TextField, Typography, useTheme } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { Add as AddIcon } from '@mui/icons-material';
import EditIcon from '@mui/icons-material/Edit';

const UpdateType = {
  ADD: 'Add',
  MODIFY: 'Modify'
}

const CollectCustomers: React.FC<{
  customers: Customer[];
  setCustomers: Dispatch<SetStateAction<Customer[]>>;
}> = ({ customers, setCustomers }) => {
  const theme = useTheme()
  const [deleteCustomerDialogOpen, setDeleteCustomerDialogOpen] = useState(false)
  const [addCustomerDialogOpen, setAddCustomerDialogOpen] = useState(false)
  const [modifyCustomerDialogOpen, setModifyCustomerDialogOpen] = useState(false)
  const [newCustomerInput, setNewCustomerInput] = useState<Customer>({
    name: '',
  });
  const [modifiedCustomerInput, setModifiedCustomerInput] = useState<Customer>({
    name: '',
  });
  const [modifiedCustomerIndex, setModifiedCustomerIndex] = useState<number>(0)

  const handleCustomersUpdate = (updateType: string) => {
    let newCustomers: Customer[] = [];
    if (updateType === UpdateType.MODIFY) {
      newCustomers = [
        // NOTE: using a modifiedCustomerIndex state here instead of passing in the customerIndex that comes when iterating through the customers array map because for some reason, the dialog keeps using the last index of the customers array when it should be using the index that is associated with the customer when the dialog was initiated.
        ...customers.slice(0, modifiedCustomerIndex),
        {
          ...modifiedCustomerInput,
          name: modifiedCustomerInput.name.trim(),
        },
        ...customers.slice(modifiedCustomerIndex + 1, customers.length),
      ];
    } else if (updateType === UpdateType.ADD) {
      newCustomers = [
        ...customers,
        {
          ...newCustomerInput,
          name: newCustomerInput.name.trim(),
        },
      ];
    }
    // console.log(customerIndex);
    setCustomers(newCustomers);
  };
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'end',
        width: '100%',
        overflow: 'hidden',
        gap: '16px'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'end',
          width: '100%',
          overflow: 'hidden',
        }}
      >
        {customers.map((customer: Customer, customerIndex: number) => {
          return (
            <Grid
              key={customer.name}
              container
              sx={{
                display: 'flex',
                alignItems: 'center',
                padding: '4px 8px',
                backgroundColor:
                  customerIndex % 2 === 0
                    ? theme.palette.primary.light
                    : 'none',
              }}
            >
              <Grid
                item
                xs={11}
              >
                <Button
                  fullWidth
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '16px',
                  }}
                  onClick={() => {
                    setModifyCustomerDialogOpen(true);
                    setModifiedCustomerInput(customer);
                    setModifiedCustomerIndex(customerIndex);
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'start',
                      alignItems: 'center',
                      gap: '16px',
                    }}
                  >
                    <Chip label={`${customerIndex + 1}`} />
                    <Typography
                      variant='body1'
                      sx={{
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        maxWidth: '10rem',
                      }}
                    >
                      {customer.name}
                    </Typography>
                  </Box>
                  <EditIcon fontSize='small' />
                </Button>
                <Dialog
                  fullWidth
                  open={modifyCustomerDialogOpen}
                >
                  <DialogContent
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      gap: '16px',
                    }}
                  >
                    <Typography variant='h6'> Modify Customer </Typography>
                    <TextField
                      label='Customer Name'
                      fullWidth
                      autoFocus
                      value={modifiedCustomerInput.name}
                      onKeyUp={(e) => {
                        if (e.key === 'Enter') {
                          handleCustomersUpdate(UpdateType.MODIFY);
                          setModifiedCustomerInput({ name: '' });
                          setModifyCustomerDialogOpen(false);
                        }
                      }}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>,
                      ) => {
                        const newModifiedCustomerInput = {
                          ...modifiedCustomerInput,
                        };
                        newModifiedCustomerInput.name = event.target.value;
                        setModifiedCustomerInput(newModifiedCustomerInput);
                      }}
                    />
                  </DialogContent>
                  <DialogActions sx={{ padding: '16px 16px 24px 0' }}>
                    <Button
                      variant='contained'
                      color='secondary'
                      // sx={{ color: theme.palette.error.contrastText }}
                      onClick={() => {
                        setModifyCustomerDialogOpen(false);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant='contained'
                      color='primary'
                      autoFocus
                      onClick={() => {
                        handleCustomersUpdate(UpdateType.MODIFY);
                        setModifiedCustomerInput({ name: '' });
                        setModifyCustomerDialogOpen(false);
                      }}
                    >
                      Save Changes
                    </Button>
                  </DialogActions>
                </Dialog>
              </Grid>
              <Grid
                item
                xs={1}
              >
                <IconButton
                  onClick={() => {
                    setDeleteCustomerDialogOpen(true);
                  }}
                >
                  <DeleteIcon color='error' />
                </IconButton>
                <Dialog
                  fullWidth
                  open={deleteCustomerDialogOpen}
                >
                  <DialogTitle
                    sx={{ display: 'flex', justifyContent: 'center' }}
                  >
                    Delete Customer?
                  </DialogTitle>
                  <DialogContent
                    sx={{ display: 'flex', justifyContent: 'center' }}
                  >
                    <Typography variant='body1'>{`${customer.name}`}</Typography>
                  </DialogContent>
                  <DialogActions
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      padding: '0 24px 16px 24px',
                    }}
                  >
                    <Button
                      variant='contained'
                      color='secondary'
                      // sx={{ color: theme.palette.error.contrastText }}
                      onClick={() => {
                        setDeleteCustomerDialogOpen(false);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant='contained'
                      color='error'
                      autoFocus
                      onClick={() => {
                        // console.log(customerIndex);
                        const newCustomers = [...customers]
                          .splice(0, customerIndex)
                          .concat(
                            [...customers].splice(
                              customerIndex + 1,
                              customers.length,
                            ),
                          );
                        setCustomers(newCustomers);
                        setDeleteCustomerDialogOpen(false);
                      }}
                    >
                      Delete
                    </Button>
                  </DialogActions>
                </Dialog>
              </Grid>
            </Grid>
          );
        })}
      </Box>
      <Button
        fullWidth
        variant='contained'
        color='secondary'
        startIcon={<AddIcon />}
        onClick={() => {
          // const newCustomers = [...customers].concat([{ name: '' }]);
          // setCustomers(newCustomers);
          setAddCustomerDialogOpen(true);
        }}
      >
        Add Customer
      </Button>
      <Dialog
        fullWidth
        open={addCustomerDialogOpen}
      >
        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: '16px',
          }}
        >
          <Typography variant='h6'> New Customer </Typography>
          <TextField
            label='Customer Name'
            fullWidth
            autoFocus
            value={newCustomerInput.name}
            onKeyUp={(e) => {
              if (e.key === 'Enter') {
                handleCustomersUpdate(UpdateType.ADD);
                setNewCustomerInput({ name: '' });
                setAddCustomerDialogOpen(false);
              }
            }}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const modifiedNewCustomerInput = { ...newCustomerInput };
              modifiedNewCustomerInput.name = event.target.value;
              setNewCustomerInput(modifiedNewCustomerInput);
            }}
          />
        </DialogContent>
        <DialogActions sx={{ padding: '16px 16px 24px 0' }}>
          <Button
            variant='contained'
            color='secondary'
            // sx={{ color: theme.palette.error.contrastText }}
            onClick={() => {
              setNewCustomerInput({ name: '' });
              setAddCustomerDialogOpen(false);
            }}
          >
            Cancel
          </Button>
          <Button
            variant='contained'
            color='primary'
            autoFocus
            onClick={() => {
              handleCustomersUpdate(UpdateType.ADD);
              setNewCustomerInput({ name: '' });
              setAddCustomerDialogOpen(false);
            }}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CollectCustomers;
